import moment from 'moment'
import { ApplicationType } from '../../models/applicationType'
import {
  isExistingAccountPaymentMethod,
  isExistingPartPayment,
  PaymentMethods,
} from '../../models/payment-methods'
import { CustomerHelper } from '../../util/customerHelper'
import { getText } from '../../internal/TextLabels'

export const createApplicationRequest = payApply => {
  const applicationRequest = {
    application: {
      applicationType:
        payApply.current.selectedPaymentMethod.type ===
          PaymentMethods.RESURS_CARD ||
        payApply.current.selectedPaymentMethod.type ===
          PaymentMethods.RESURS_REVOLVING_CREDIT
          ? ApplicationType.LIMIT_INCREASE
          : ApplicationType.NEW_ACCOUNT,
      periodOfRepayment: payApply.current.periodOfRepayment,
      requestedAmount: payApply.current.selectedLimit,
      applicationData: payApply.formSpec.formValues,
    },
    channelType: 'MERCHANT_PORTAL',
    customer: {
      deviceInfo: {
        ip: '127.0.0.1',
      },
      email: payApply.formSpec.formValues['applicant-email-address'],
      governmentId:
        payApply.formSpec.formValues['applicant-government-id'] ||
        CustomerHelper.fromPayApply(payApply).governmentId,
      mobile: payApply.formSpec.formValues['applicant-mobile-number'],
      phone:
        payApply.formSpec.formValues['applicant-telephone-number'] !== null
          ? payApply.formSpec.formValues['applicant-telephone-number']
          : CustomerHelper.fromPayApply(payApply).aPhoneNumber,
    },
    paymentMethodId: payApply.current.selectedPaymentMethod.id,
  }
  if (
    payApply.current.selectedPaymentMethod.type ===
      PaymentMethods.RESURS_CARD ||
    payApply.current.selectedPaymentMethod.type ===
      PaymentMethods.RESURS_REVOLVING_CREDIT
  ) {
    const { accountNumber, cardNumber } = payApply.current.account || {}
    applicationRequest.account = {
      accountNumber:
        !cardNumber || cardNumber.includes('X') ? accountNumber : cardNumber,
    }
  }
  if (payApply.pushedOutToCustomer) {
    applicationRequest.publicSettings = {
      pushedOutToCustomer: true,
      sendLinks: true,
      automaticFlowCompletion: true,
    }
  } else {
    applicationRequest.publicSettings = {
      sendLinks: false,
    }
  }

  return JSON.stringify(applicationRequest)
}

export const createPaymentRequest = (payApply, { username }) => {
  const paymentRequest = {
    cart: {
      cartItems: [
        {
          externalId: 'id',
          reference: 'ref',
          quantity: 1,
          unit: 'st',
          unitPriceIncVat: payApply.current.selectedAmount,
          vatRate: 0,
          description: 'desc',
          type: 'NORMAL',
        },
      ],
    },
    channelType: 'MERCHANT_PORTAL',
    customer: {
      deviceInfo: {
        ip: '127.0.0.1',
      },
      email: payApply.formSpec.formValues['applicant-email-address'],
      governmentId:
        payApply.formSpec.formValues['applicant-government-id'] ||
        CustomerHelper.fromPayApply(payApply).governmentId,
      mobile: payApply.formSpec.formValues['applicant-mobile-number'],
      phone:
        payApply.formSpec.formValues['applicant-telephone-number'] !== null
          ? payApply.formSpec.formValues['applicant-telephone-number']
          : CustomerHelper.fromPayApply(payApply).aPhoneNumber,
    },
    paymentMethodId: payApply.current.selectedPaymentMethod.id,
    ...(payApply.payment.order
      ? { externalId: payApply.payment.order.orderId }
      : {}),
  }

  if (
    payApply.current.selectedCard &&
    payApply.current.selectedLimit > payApply.current.selectedCard.usableAmount
  ) {
    paymentRequest.application = {
      applicationType: ApplicationType.LIMIT_INCREASE,
      applicationData: payApply.formSpec.formValues,
      requestedAmount: payApply.current.selectedLimit,
    }
  }
  if (
    payApply.current.selectedPaymentMethod.type ===
      PaymentMethods.RESURS_NEW_CARD ||
    (payApply.current.selectedPaymentMethod.type ===
      PaymentMethods.RESURS_PART_PAYMENT &&
      !isExistingPartPayment(payApply.current.selectedPaymentMethod)) ||
    payApply.current.selectedPaymentMethod.type ===
      PaymentMethods.RESURS_NEW_REVOLVING_CREDIT
  ) {
    paymentRequest.application = {
      applicationType: ApplicationType.NEW_ACCOUNT,
      applicationData: payApply.formSpec.formValues,
      periodOfRepayment: payApply.current.periodOfRepayment,
      requestedAmount: payApply.current.selectedLimit,
    }
  }
  if (isExistingAccountPaymentMethod(payApply.current.selectedPaymentMethod)) {
    const { accountNumber, cardNumber } = payApply.current.account || {}
    paymentRequest.account = {
      accountNumber:
        !cardNumber || cardNumber.includes('X') ? accountNumber : cardNumber,
    }
  }

  if (payApply.pushedOutToCustomer) {
    paymentRequest.publicSettings = {
      automaticDebit: payApply.publicSettings.automaticDebit,
      pushedOutToCustomer: true,
      automaticFlowCompletion: true,
      sendLinks: true,
    }
    paymentRequest.settings = {
      creator: username,
      waitForFraud: true,
      annulFrozen: true,
    }
  } else {
    paymentRequest.publicSettings = {
      sendLinks: false,
    }
  }
  return JSON.stringify(paymentRequest)
}

const buildInvoiceCart = invoice => {
  const cartItems = []
  invoice.orderLines.forEach(orderItem => {
    if (orderItem.articleNbr !== '') {
      cartItems.push({
        externalId: orderItem.articleNbr,
        reference: orderItem.articleNbr,
        quantity: orderItem.quantity,
        unit: 'st',
        unitPriceIncVat: orderItem.unitPriceExVat * (1 + orderItem.vat / 100),
        vatRate: orderItem.vat / 100,
        description: orderItem.description,
        type: 'NORMAL',
      })
    }
  })

  const cart = {
    cartItems,
    orderId: invoice.orderId,
    totalExVat: invoice.totalExVat,
    totalVat: 0,
    totalWithVat: invoice.totalAmount,
  }
  return cart
}

export const createInvoiceRequest = payApply => {
  const invoiceCart = buildInvoiceCart(payApply.payment.order)
  const invoiceRequest = {
    cart: invoiceCart,
    account: null,
    application: {
      applicationType: ApplicationType.NEW_ACCOUNT,
      requestedAmount: payApply.current.selectedAmount,
      applicationData: payApply.formSpec.formValues,
    },
    invoiceProjectId: payApply.payment.order.metaData.externalRef,
    channelType: 'MERCHANT_PORTAL',
    customer: {
      deviceInfo: {
        ip: '127.0.0.1',
      },
      email: payApply.formSpec.formValues['applicant-email-address'],
      governmentId:
        payApply.formSpec.formValues['applicant-government-id'] ||
        CustomerHelper.fromPayApply(payApply).governmentId,
      mobile: payApply.formSpec.formValues['applicant-mobile-number'],
      phone:
        payApply.formSpec.formValues['applicant-telephone-number'] !== null
          ? payApply.formSpec.formValues['applicant-telephone-number']
          : CustomerHelper.fromPayApply(payApply).aPhoneNumber,
    },
    paymentMethodId: payApply.current.selectedPaymentMethod.id,
    externalId: payApply.payment.order.orderId,
  }

  if (payApply.pushedOutToCustomer) {
    invoiceRequest.publicSettings = {
      automaticFlowCompletion: true,
      pushedOutToCustomer: true,
      sendLinks: true,
    }
  } else {
    invoiceRequest.publicSettings = {
      sendLinks: false,
    }
  }

  return JSON.stringify(invoiceRequest)
}

export const buildTransactionCart = cartItems =>
  cartItems.map(item => {
    const {
      externalId,
      reference,
      type,
      unit = '',
      vatRate,
      deliveryDate,
    } = item
    let { unitPriceIncVat, quantity, description } = item

    if (item.newQuantity) {
      quantity = item.newQuantity
    } else if (item.newSum) {
      description = item.newSumDescription
      quantity = 1
      unitPriceIncVat = item.newSum
    } else if (item.newAmount) {
      quantity = 1
      unitPriceIncVat = item.newAmount
    }
    return {
      description,
      externalId,
      quantity,
      reference,
      type,
      unit,
      unitPriceIncVat,
      vatRate,
      deliveryDate,
    }
  })

export const annulTransactionRequest = (cartData, details, auth) => {
  const annulRequest = {
    cart: {
      cartItems: buildTransactionCart(cartData),
    },
    channelType: details.channelType,
    creator: auth.username,
  }
  return JSON.stringify(annulRequest)
}

export const addToCartRequest = (cartData, id, details, auth) => {
  const addRequest = {
    cart: {
      cartItems: [
        {
          description: cartData.description,
          externalId: cartData.externalId,
          quantity: cartData.quantity,
          reference: cartData.reference,
          totalAmountIncVat: cartData.totalAmountIncVat,
          type: 'NORMAL',
          unit: cartData.unit,
          unitPriceIncVat: cartData.costForOneUnitWithVat,
          vatRate: cartData.vatRate,
        },
      ],
      orderId: id,
      totalExVat: 0,
      totalIncVat: 0,
      totalVat: 0,
    },
    channelType: details.channelType,
    creator: auth.username,
  }
  return JSON.stringify(addRequest)
}

export const debitTransactionRequest = (
  cartData,
  details,
  auth,
  invoiceRef,
  hasInvoiceNumber,
) => {
  let debitRequest
  if (
    details.paymentMethod.type === PaymentMethods.RESURS_INVOICE ||
    details.paymentMethod.type === PaymentMethods.RESURS_INVOICE_ACCOUNT
  ) {
    debitRequest = {
      cart: {
        cartItems: buildTransactionCart(cartData),
      },
      channelType: details.channelType,
      creator: auth.username,
      invoiceData: {
        invoiceId: invoiceRef,
        deliveryType: 'EMAIL',
      },
    }
    if (hasInvoiceNumber || invoiceRef === '') {
      delete debitRequest.invoiceData.invoiceId
    }
  } else {
    debitRequest = {
      cart: {
        cartItems: buildTransactionCart(cartData),
      },
      channelType: details.channelType,
      creator: auth.username,
    }
  }

  return JSON.stringify(debitRequest)
}

export const creditTransactionRequest = (
  cartData,
  details,
  auth,
  invoiceRef,
) => {
  let creditRequest
  if (
    details.paymentMethod.type === PaymentMethods.RESURS_INVOICE ||
    details.paymentMethod.type === PaymentMethods.RESURS_INVOICE_ACCOUNT
  ) {
    creditRequest = {
      cart: {
        cartItems: buildTransactionCart(cartData),
      },
      channelType: details.channelType,
      creator: auth.username,
      creditNoteData: {
        creditNoteId: invoiceRef,
        deliveryType: 'EMAIL',
      },
    }
    if (invoiceRef === '') {
      delete creditRequest.creditNoteData.creditNoteId
    }
  } else {
    creditRequest = {
      cart: {
        cartItems: buildTransactionCart(cartData),
      },
      channelType: details.channelType,
      creator: auth.username,
    }
  }
  return JSON.stringify(creditRequest)
}

export const debitPaymentRequest = (payApply, auth) => {
  const debitRequest = {
    channelType: 'MERCHANT_PORTAL',
    creator: auth.username,
  }

  if (payApply.current.debitReference) {
    debitRequest.transactionId = payApply.current.debitReference
  }

  if (
    payApply.current.selectedPaymentMethod &&
    payApply.current.selectedPaymentMethod.type ===
      PaymentMethods.RESURS_INVOICE
  ) {
    debitRequest.invoiceData = {
      deliveryType: 'EMAIL',
    }
    if (!auth.hasInvoiceNumber) {
      debitRequest.invoiceData.invoiceId = payApply.payment.order.orderId
    }
  }

  return JSON.stringify(debitRequest)
}

export const createSearchBody = requestData => {
  switch (requestData.filter) {
    case 'frozen':
      return {
        paymentSearchCriteria: {
          fields: [
            'PAYMENT_EXTERNAL_ID',
            'CUSTOMER_GOVERNMENT_ID',
            'CUSTOMER_FULL_NAME',
            'PAYMENT_STATUS',
            'PAYMENT_TOTAL_VALUE',
            'PAYMENT_CREATED',
          ],
          searchString: requestData.searchTerm,
        },
        frozen: true,
      }
    case 'debit':
      return {
        paymentSearchCriteria: {
          fields: [
            'PAYMENT_EXTERNAL_ID',
            'CUSTOMER_GOVERNMENT_ID',
            'CUSTOMER_FULL_NAME',
            'PAYMENT_STATUS',
            'PAYMENT_TOTAL_VALUE',
            'PAYMENT_CREATED',
          ],
          searchString: requestData.searchTerm,
        },
        hasStatuses: ['DEBITABLE'],
        frozen: false,
        // TA BORT?
        authorizedFrom: '2018-05-01T13:22:11.495Z',
      }
    default:
      return {
        paymentSearchCriteria: {
          fields: [
            'PAYMENT_EXTERNAL_ID',
            'CUSTOMER_GOVERNMENT_ID',
            'CUSTOMER_FULL_NAME',
            'PAYMENT_STATUS',
            'PAYMENT_TOTAL_VALUE',
            'PAYMENT_CREATED',
          ],
          searchString: requestData.searchTerm,
        },
        // TA BORT?
        authorizedFrom: '2018-05-01T13:22:11.495Z',
      }
  }
}

export const updateOrderDataAndFeeRequest = paymentMethods => {
  const updatedPaymentMethods = []
  const orders = paymentMethods.map(pm => pm.displayOrder).sort((a, b) => a - b)

  paymentMethods.forEach((paymentMethod, index) => {
    let fee = null
    if (paymentMethod.fee && paymentMethod.fee > 0) {
      const articleNumber =
        paymentMethod.fees && paymentMethod.fees[0].articleNumber
          ? paymentMethod.fees[0].articleNumber
          : 'pay-fee'

      const description =
        paymentMethod.fees && paymentMethod.fees[0].description
          ? paymentMethod.fees[0].description
          : getText('paymentSettings.paymentMethods.paymentMethodFee')

      fee = {
        articleNumber,
        description,
        amountWithoutVat: paymentMethod.fee,
      }
    }

    updatedPaymentMethods.push({
      paymentMethodId: paymentMethod.id,
      order: orders[index],
      validFrom: `${moment(paymentMethod.validFrom, 'YYYY-MM-DD').format(
        'YYYY-MM-DD',
      )}T00:00:00.000Z`,
      validTo: `${moment(paymentMethod.validTo, 'YYYY-MM-DD').format(
        'YYYY-MM-DD',
      )}T00:00:00.000Z`,
      fee,
    })
  })

  return {
    paymentMethods: updatedPaymentMethods,
  }
}
